import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader from '../components/StaffHeader';
import { inZone } from '../utils/companyDate';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { Link } from 'react-router-dom';
import { PRODUCT_TYPE } from '../config/product';
import { formatCurrency } from '../utils/formatCurrency';
import { getPlanName } from '../config/plans';
import { startCheckout, billingErrorMessage } from '../utils/billing';
import LapsedBanner from '../components/LapsedBanner';
import GettingStartedCard from '../components/GettingStartedCard';
import SeasonCard from '../components/SeasonCard';
import IntakeSignupsNotice, { useIntakeSignups } from '../components/IntakeSignupsNotice';
import ServiceRequestsNotice, { useServiceRequestsNotice } from '../components/ServiceRequestsNotice';

// How long the post-checkout confirmation stays up before dismissing itself.
const SUBSCRIPTION_BANNER_MS = 10000;

// After a Checkout return, the subscription state is polled this often, for at
// most this long, until the webhook has promoted the company. Bounded on
// purpose: a webhook that never lands must not leave a loop running for the
// page's lifetime.
const CHECKOUT_POLL_MS = 2000;
const CHECKOUT_POLL_LIMIT_MS = 30000;

// Shape of the session id Stripe substitutes into the success URL. Anything
// else in the address bar is not looked up.
const CHECKOUT_SESSION_ID = /^cs_(test|live)_[A-Za-z0-9]+$/;

// The trial storm's end, in the company's time zone, e.g. "Tue, Dec 2, 3:15 PM".
const formatTrialStormEnd = (value, timezone) =>
  new Date(value).toLocaleString([], inZone(timezone, {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }));

function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, isDriver, isOnTrial, isTrialExpired, isTrialStormInProgress, getTrialStormEndsAt, TRIAL_STORM_LIMIT, refreshUser, timezone } = useAuth();
  const { getAppName } = useConfig();
  const [stats, setStats] = useState({
    totalRoutes: 0,
    activeRoutes: 0,
    totalCustomers: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);

  // Post-checkout banner. 'success' or 'cancelled', read once from the query
  // string Stripe redirected back with and then held in state so the URL can
  // be cleaned up - a reload should not replay the banner.
  const [subscriptionNotice, setSubscriptionNotice] = useState(null);
  // The plan the Checkout session says was bought, { planId, planName }, or
  // null when the lookup did not answer. The banner names this rather than the
  // cached tier, which is stale until the webhook lands.
  const [purchasedPlan, setPurchasedPlan] = useState(null);
  // "Activate your plan" prompt for someone who picked a plan on the pricing
  // page before signing up.
  const [activateError, setActivateError] = useState('');
  const [activating, setActivating] = useState(false);
  const [activateDismissed, setActivateDismissed] = useState(false);

  // Weather state
  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(null);
  const [weatherView, setWeatherView] = useState('daily'); // 'daily' or 'hourly'
  const [weatherSettings, setWeatherSettings] = useState(null);
  const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
  const [weatherLocationName, setWeatherLocationName] = useState(null);

  // Financial summary state
  const [financialSummary, setFinancialSummary] = useState(null);
  const [financialSummaryLoading, setFinancialSummaryLoading] = useState(true);
  const [financialSummaryError, setFinancialSummaryError] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState('this_month');

  // The Total Routes and Total Stops tiles, from GET /api/dashboard/stats.
  // tilePeriod is this | last | all; this is the company's current season (or
  // the most recent one, off-season), from the months in Settings > Company > Season.
  const [tilePeriod, setTilePeriod] = useState('this');
  const [tileStats, setTileStats] = useState(null);
  const [tileStatsLoading, setTileStatsLoading] = useState(true);

  // How many customers and properties are waiting for the owner to confirm the
  // driveway size the customer stated. Zero hides the banner entirely. The
  // items are the first few of them, named, so the banner can link to each.
  const [sizeConfirmationCount, setSizeConfirmationCount] = useState(0);
  const [sizeConfirmationItems, setSizeConfirmationItems] = useState([]);
  // Intake signups not yet reviewed (migration 078). Owner and admin only.
  const {
    items: intakeSignups,
    setItems: setIntakeSignups,
    reviewingId,
    error: intakeSignupsError,
    markReviewed: markIntakeSignupReviewed,
  } = useIntakeSignups();
  // On-call customers' portal requests since the notice was last dismissed.
  const {
    notice: serviceRequestsNotice,
    dismissing: dismissingServiceRequests,
    error: serviceRequestsNoticeError,
    load: loadServiceRequestsNotice,
    dismiss: dismissServiceRequests,
  } = useServiceRequestsNotice();

  const loadTileStats = useCallback(async (period = tilePeriod) => {
    try {
      setTileStatsLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/dashboard/stats?period=${period}&product=${PRODUCT_TYPE}`);
      setTileStats(response.data);
    } catch (err) {
      console.error('Failed to load route and stop counts:', err);
      setTileStats(null);
    } finally {
      setTileStatsLoading(false);
    }
  }, [tilePeriod]);

  useEffect(() => {
    loadTileStats();
  }, [loadTileStats]);

  const loadDashboardData = useCallback(async () => {
    try {
      // Load routes and customers in parallel
      //
      // The size-confirmation count rides in the same Promise.all rather than a
      // third sequential await, and carries its OWN catch. Promise.all rejects
      // as a whole on the first rejection, so without that catch a failing count
      // would blank the entire dashboard to hide one banner. Swallowing it to 0
      // leaves the banner hidden, which is the same posture the held-signups
      // fetch on the Customers page already takes. A driver gets a 403 here,
      // which lands in the same catch.
      // The new-signups list rides along on the same terms as the size count:
      // its own catch, so a failure hides one card instead of the dashboard.
      const [routesResponse, customersResponse, sizePendingResponse, intakeSignupsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/routes?product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers?product=${PRODUCT_TYPE}`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers/size-confirmation-pending`)
          .catch(() => ({ data: { count: 0, items: [] } })),
        axios.get(`${process.env.REACT_APP_API_URL}/api/customers/intake-signups`)
          .catch(() => ({ data: { count: 0, items: [] } }))
      ]);
      const routes = routesResponse.data;
      const customers = customersResponse.data;
      setSizeConfirmationCount(Number(sizePendingResponse?.data?.count) || 0);
      setSizeConfirmationItems(Array.isArray(sizePendingResponse?.data?.items) ? sizePendingResponse.data.items : []);
      setIntakeSignups(Array.isArray(intakeSignupsResponse?.data?.items) ? intakeSignupsResponse.data.items : []);

      // Calculate stats
      setStats({
        totalRoutes: routes.length,
        activeRoutes: routes.filter(r => r.status === 'in_progress').length,
        totalCustomers: customers.length,
      });
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setDashboardError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [setIntakeSignups]);

  // Owner and admin only, like the endpoint; a failure just hides the notice.
  useEffect(() => {
    if (isAdmin()) loadServiceRequestsNotice();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadServiceRequestsNotice]);

  const refreshDashboard = useCallback(
    () => Promise.all([loadDashboardData(), loadTileStats(), isAdmin() ? loadServiceRequestsNotice() : null]),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadDashboardData, loadTileStats, loadServiceRequestsNotice]
  );
  const { PullToRefreshIndicator } = usePullToRefresh(refreshDashboard);

  // Review opens that customer's edit form on the Customers page. Saving it is
  // what marks the signup reviewed, so the card is not changed here; it is
  // reloaded when the owner comes back to the Dashboard.
  const reviewIntakeSignup = (item) => {
    navigate('/customers', {
      state: {
        focusCustomerId: item.id,
        focusIsActive: item.isActive,
        focusIntakeStatus: item.intakeStatus,
        focusOpenEdit: true,
      },
    });
  };

  // Load weather data using coordinates
  const loadWeather = useCallback(async (lat, lon, locationLabel = null) => {
    try {
      setWeatherLoading(true);
      setWeatherError(null);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/weather?lat=${lat}&lon=${lon}`
      );
      setWeather(response.data);
      // Set location name - use provided label or from API response
      if (locationLabel) {
        setWeatherLocationName(locationLabel);
      } else if (response.data?.current?.location) {
        setWeatherLocationName(response.data.current.location);
      }
    } catch (err) {
      console.error('Failed to load weather:', err);
      setWeatherError('Failed to load weather data');
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  // Load financial summary for admins
  const loadFinancialSummary = useCallback(async (period = financialPeriod) => {
    try {
      setFinancialSummaryLoading(true);
      setFinancialSummaryError(null);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/financial-summary?period=${period}&product=${PRODUCT_TYPE}`);
      setFinancialSummary(response.data);
    } catch (err) {
      console.error('Failed to load financial summary:', err);
      setFinancialSummaryError('Failed to load financial data');
    } finally {
      setFinancialSummaryLoading(false);
    }
  }, [financialPeriod]);

  useEffect(() => {
    loadDashboardData();
    // Only load financial summary for admin users
    if (isAdmin()) {
      loadFinancialSummary();
    }
  }, [loadDashboardData, loadFinancialSummary, isAdmin]);

  // Handle the return trip from Stripe Checkout.
  //
  // The webhook is what actually promotes the company, and Stripe posts it
  // independently of the browser redirect, so it can land seconds after the
  // customer is back here. Two consequences, handled separately:
  //
  // - The banner names the plan from the Checkout session (session_id on the
  //   success URL), not from the cached user, which still holds the old tier
  //   until the webhook lands. If the lookup does not answer (no id, not this
  //   company's session, Stripe unreachable) it falls back to the cached tier
  //   as before. The banner waits for the lookup so it never shows one plan
  //   and then another.
  // - The subscription state is polled every CHECKOUT_POLL_MS, for at most
  //   CHECKOUT_POLL_LIMIT_MS, until the company is active on the plan bought
  //   (or simply active, when the plan is unknown); then the user is
  //   refreshed once, so the header and any FeatureGate catch up without a
  //   reload. Stops early on unmount.
  //
  // Both query params are dropped once the lookup has used the session id,
  // so a refresh or a shared link does not replay the banner.
  useEffect(() => {
    const flag = searchParams.get('subscription');
    if (flag !== 'success' && flag !== 'cancelled') return undefined;

    const dropParams = () => {
      const next = new URLSearchParams(searchParams);
      next.delete('subscription');
      next.delete('session_id');
      setSearchParams(next, { replace: true });
    };

    if (flag !== 'success') {
      setSubscriptionNotice(flag);
      dropParams();
      return undefined;
    }

    const sessionId = searchParams.get('session_id');
    let cancelled = false;
    let timer = null;
    const wait = (ms) => new Promise((resolve) => { timer = setTimeout(resolve, ms); });

    const lookupPlan = async () => {
      if (!sessionId || !CHECKOUT_SESSION_ID.test(sessionId)) return null;
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/billing/checkout-session/${encodeURIComponent(sessionId)}`,
          { timeout: 8000 }
        );
        return response.data && response.data.planId && response.data.planName ? response.data : null;
      } catch (err) {
        // Missing, not ours, or Stripe unreachable: fall back to the cached tier.
        return null;
      }
    };

    // The poll reads /api/billing/status, not /me: /api/auth sits behind the
    // login limiter (10 requests per 15 minutes), so polling /me would lock
    // the customer out of logging in. billing/status reads the same
    // webhook-synced columns under the general limiter. /me is called once up
    // front, as before, and once more when the company has caught up, so the
    // cached user (header, FeatureGate) picks up the new tier.
    const readStatus = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/status`, { timeout: 8000 });
        return { subscriptionTier: response.data.tier, subscriptionStatus: response.data.status };
      } catch (err) {
        return null;
      }
    };

    const caughtUp = (profile, plan) => Boolean(profile)
      && profile.subscriptionStatus === 'active'
      && (!plan || profile.subscriptionTier === plan.planId);

    (async () => {
      const deadline = Date.now() + CHECKOUT_POLL_LIMIT_MS;
      const firstRefresh = refreshUser({ silent: true });
      const plan = await lookupPlan();
      if (cancelled) return;
      if (plan) setPurchasedPlan(plan);
      setSubscriptionNotice('success');
      dropParams();

      if (caughtUp(await firstRefresh, plan)) return;
      while (!cancelled && Date.now() + CHECKOUT_POLL_MS <= deadline) {
        await wait(CHECKOUT_POLL_MS);
        if (cancelled) return;
        if (caughtUp(await readStatus(), plan)) {
          if (!cancelled) refreshUser({ silent: true });
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // searchParams is intentionally the only trigger: this runs on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss the banner. Separate from the effect above so the timer is
  // tied to the banner being visible rather than to the URL.
  useEffect(() => {
    if (!subscriptionNotice) return undefined;
    const timer = setTimeout(() => setSubscriptionNotice(null), SUBSCRIPTION_BANNER_MS);
    return () => clearTimeout(timer);
  }, [subscriptionNotice]);

  // The plan this company picked at signup but has never paid for. Shown only
  // to someone who can actually start checkout (owner/admin, matching the
  // gate on POST /api/billing/checkout) and only while they are still on the
  // trial - once a subscription is live there is nothing to activate.
  const pendingPlan = user?.requestedTier || null;
  const showActivatePrompt =
    !activateDismissed &&
    Boolean(pendingPlan) &&
    pendingPlan !== 'enterprise' &&
    isAdmin() &&
    isOnTrial();

  const handleActivatePlan = async () => {
    setActivateError('');
    setActivating(true);
    try {
      await startCheckout(pendingPlan);
    } catch (err) {
      console.error('Plan activation failed:', err);
      setActivateError(
        billingErrorMessage(err, 'Could not start checkout. Please try again from the Pricing page.')
      );
      setActivating(false);
    }
  };

  // Handle period change for financial summary
  const handleFinancialPeriodChange = (e) => {
    const newPeriod = e.target.value;
    setFinancialPeriod(newPeriod);
    loadFinancialSummary(newPeriod);
  };

  // Load weather settings and then weather data
  useEffect(() => {
    const loadWeatherData = async () => {
      try {
        // First, load weather settings from company
        const settingsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/company`);
        const settings = {
          weatherUseHome: settingsResponse.data?.weather_use_home || false,
          weatherCity: settingsResponse.data?.weather_city || '',
          weatherState: settingsResponse.data?.weather_state || '',
        };
        setWeatherSettings(settings);

        // If using home location and city/state are set, geocode and load weather
        if (settings.weatherUseHome && settings.weatherCity && settings.weatherState) {
          try {
            const geocodeResponse = await axios.get(
              `${process.env.REACT_APP_API_URL}/api/weather/geocode?city=${encodeURIComponent(settings.weatherCity)}&state=${encodeURIComponent(settings.weatherState)}`
            );
            const locationLabel = `${settings.weatherCity}, ${settings.weatherState}`;
            await loadWeather(geocodeResponse.data.lat, geocodeResponse.data.lon, locationLabel);
            setUsingCurrentLocation(false);
          } catch (err) {
            console.error('Geocode error, falling back to GPS:', err);
            // Fall back to GPS
            loadWeatherFromGPS();
          }
        } else {
          // Use GPS location
          loadWeatherFromGPS();
        }
      } catch (err) {
        console.error('Failed to load weather settings:', err);
        // Fall back to GPS
        loadWeatherFromGPS();
      }
    };

    const loadWeatherFromGPS = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            loadWeather(position.coords.latitude, position.coords.longitude);
            setUsingCurrentLocation(true);
          },
          (error) => {
            console.error('Geolocation error:', error);
            setWeatherError('Enable location to see weather');
            setWeatherLoading(false);
          },
          { timeout: 10000, maximumAge: 300000 } // 5 min cache
        );
      } else {
        setWeatherError('Location not supported');
        setWeatherLoading(false);
      }
    };

    loadWeatherData();
  }, [loadWeather]);

  // Handler for switching to current GPS location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setWeatherLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          loadWeather(position.coords.latitude, position.coords.longitude);
          setUsingCurrentLocation(true);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setWeatherError('Could not get current location');
          setWeatherLoading(false);
        },
        { timeout: 10000 }
      );
    }
  };

  // Handler for switching back to home location
  const handleUseHomeLocation = async () => {
    if (weatherSettings?.weatherCity && weatherSettings?.weatherState) {
      setWeatherLoading(true);
      try {
        const geocodeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/weather/geocode?city=${encodeURIComponent(weatherSettings.weatherCity)}&state=${encodeURIComponent(weatherSettings.weatherState)}`
        );
        const locationLabel = `${weatherSettings.weatherCity}, ${weatherSettings.weatherState}`;
        await loadWeather(geocodeResponse.data.lat, geocodeResponse.data.lon, locationLabel);
        setUsingCurrentLocation(false);
      } catch (err) {
        console.error('Failed to load home location weather:', err);
        setWeatherError('Could not load home location weather');
        setWeatherLoading(false);
      }
    }
  };

  // Get weather icon based on condition code
  // Uses OpenWeatherMap icon codes for compatibility (NWS API maps to these)
  const getWeatherIcon = (iconCode) => {
    // Map icon codes to emoji fallbacks if image fails
    const iconMap = {
      '01d': { url: 'https://openweathermap.org/img/wn/01d@2x.png', emoji: '☀️' },
      '01n': { url: 'https://openweathermap.org/img/wn/01n@2x.png', emoji: '🌙' },
      '02d': { url: 'https://openweathermap.org/img/wn/02d@2x.png', emoji: '⛅' },
      '02n': { url: 'https://openweathermap.org/img/wn/02n@2x.png', emoji: '☁️' },
      '03d': { url: 'https://openweathermap.org/img/wn/03d@2x.png', emoji: '☁️' },
      '03n': { url: 'https://openweathermap.org/img/wn/03n@2x.png', emoji: '☁️' },
      '04d': { url: 'https://openweathermap.org/img/wn/04d@2x.png', emoji: '☁️' },
      '04n': { url: 'https://openweathermap.org/img/wn/04n@2x.png', emoji: '☁️' },
      '09d': { url: 'https://openweathermap.org/img/wn/09d@2x.png', emoji: '🌧️' },
      '09n': { url: 'https://openweathermap.org/img/wn/09n@2x.png', emoji: '🌧️' },
      '10d': { url: 'https://openweathermap.org/img/wn/10d@2x.png', emoji: '🌦️' },
      '10n': { url: 'https://openweathermap.org/img/wn/10n@2x.png', emoji: '🌧️' },
      '11d': { url: 'https://openweathermap.org/img/wn/11d@2x.png', emoji: '⛈️' },
      '11n': { url: 'https://openweathermap.org/img/wn/11n@2x.png', emoji: '⛈️' },
      '13d': { url: 'https://openweathermap.org/img/wn/13d@2x.png', emoji: '❄️' },
      '13n': { url: 'https://openweathermap.org/img/wn/13n@2x.png', emoji: '❄️' },
      '50d': { url: 'https://openweathermap.org/img/wn/50d@2x.png', emoji: '🌫️' },
      '50n': { url: 'https://openweathermap.org/img/wn/50n@2x.png', emoji: '🌫️' }
    };
    const icon = iconMap[iconCode] || iconMap['03d'];
    return icon.url;
  };

  // Format date for forecast
  const formatForecastDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Format time for hourly forecast (handles ISO format from NWS)
  const formatHourlyTime = (timeStr) => {
    const date = new Date(timeStr);
    const now = new Date();
    const hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12;

    // Show "Now" for current hour
    if (date.getHours() === now.getHours() && date.getDate() === now.getDate()) {
      return 'Now';
    }
    return `${hour12}${ampm}`;
  };

  // Calculate total accumulated snowfall from forecast
  const getTotalSnowfall = (forecast, isHourly = false) => {
    if (!forecast || forecast.length === 0) return 0;
    const total = forecast.reduce((sum, item) => sum + parseFloat(item.snow || 0), 0);
    return total.toFixed(1);
  };

  // Group hourly forecast by day
  const groupHourlyByDay = (hourlyData) => {
    if (!hourlyData || hourlyData.length === 0) return [];

    const groups = [];
    let currentDay = null;
    let currentGroup = null;

    for (const hour of hourlyData) {
      const date = new Date(hour.time);
      const dayKey = date.toDateString();

      if (dayKey !== currentDay) {
        if (currentGroup) groups.push(currentGroup);
        currentDay = dayKey;
        currentGroup = {
          label: formatForecastDate(hour.time),
          hours: []
        };
      }
      currentGroup.hours.push(hour);
    }
    if (currentGroup) groups.push(currentGroup);

    return groups;
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <StaffHeader active="dashboard" />

      <style>{`
        .app-header-content {
          overflow: hidden;
        }
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 1;
          overflow: hidden;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }
        /* Mobile Menu */
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }
        @media (max-width: 900px) {
          .desktop-nav {
            display: none;
          }
        }

        /* Weather Widget */
        .weather-widget {
          background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%);
          border-radius: var(--radius-lg);
          padding: 20px;
          margin-top: 20px;
          color: white;
          box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3);
        }
        .weather-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255,255,255,0.2);
        }
        .weather-header-location {
          font-weight: 600;
          font-size: 1rem;
        }
        .weather-location-toggle {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .weather-location-toggle:hover {
          background: rgba(255,255,255,0.3);
        }
        .weather-loading, .weather-error {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 20px;
          color: rgba(255,255,255,0.9);
        }
        .weather-error-icon {
          font-size: 1.5rem;
        }
        .weather-current {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .weather-icon-large {
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }
        .weather-temp-main {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 120px;
        }
        .weather-temp {
          font-size: 2.5rem;
          font-weight: 700;
          line-height: 1;
        }
        .weather-condition {
          font-size: 1rem;
          text-transform: capitalize;
          opacity: 0.9;
          margin-top: 4px;
        }
        .weather-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 0.875rem;
          opacity: 0.9;
        }
        .weather-location {
          font-weight: 600;
          font-size: 1rem;
        }
        .weather-forecast {
          display: flex;
          justify-content: space-between;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid rgba(255,255,255,0.2);
          gap: 8px;
          overflow-x: auto;
        }
        .forecast-day {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          min-width: 60px;
          flex: 1;
        }
        .forecast-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          opacity: 0.8;
        }
        .forecast-icon {
          width: 40px;
          height: 40px;
        }
        .forecast-temp {
          font-size: 1rem;
          font-weight: 600;
        }
        .forecast-snow {
          font-size: 0.7rem;
          background: rgba(255,255,255,0.2);
          padding: 2px 6px;
          border-radius: 8px;
          white-space: nowrap;
        }
        .weather-snow-current {
          font-size: 0.85rem;
          background: rgba(255,255,255,0.2);
          padding: 4px 10px;
          border-radius: 12px;
          margin-top: 6px;
        }
        .weather-toggle {
          display: flex;
          justify-content: center;
          gap: 4px;
          margin-top: 16px;
          background: rgba(0,0,0,0.15);
          padding: 4px;
          border-radius: 8px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        .weather-toggle-btn {
          padding: 6px 16px;
          border: none;
          background: transparent;
          color: rgba(255,255,255,0.7);
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .weather-toggle-btn.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .weather-toggle-btn:hover:not(.active) {
          color: white;
        }
        .snow-total-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: rgba(255,255,255,0.15);
          padding: 10px 16px;
          border-radius: 10px;
          margin-top: 12px;
        }
        /* Post-checkout banners and the activate-plan prompt */
        .subscription-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          border-radius: 10px;
          padding: 14px 16px;
          margin-bottom: var(--spacing-md);
          font-size: 0.95rem;
          cursor: pointer;
        }
        .subscription-banner-success {
          background: #ecfdf5;
          border: 1px solid #6ee7b7;
          color: #065f46;
        }
        .subscription-banner-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e3a8a;
        }
        /* Amber taken from .activate-plan-card below, which is this page's
           existing treatment for "something is waiting on you". Same four
           colours, so the two read as one family rather than two opinions. */
        .subscription-banner-warning {
          background: #fffbeb;
          border: 1px solid #fcd34d;
          color: #78350f;
        }
        .subscription-banner-warning strong {
          color: #92400e;
        }
        .size-pending-list {
          list-style: none;
          margin: 8px 0 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          gap: 6px 14px;
        }
        .size-pending-link {
          background: none;
          border: none;
          padding: 0;
          font: inherit;
          color: #92400e;
          font-weight: 600;
          text-decoration: underline;
          cursor: pointer;
          text-align: left;
        }
        .size-pending-link:hover {
          color: #78350f;
        }
        .size-pending-more {
          color: #78350f;
        }
        .subscription-banner-icon {
          font-size: 1.25rem;
        }
        .subscription-banner div {
          flex: 1;
        }
        .subscription-banner-close {
          background: none;
          border: none;
          color: inherit;
          font-size: 1.4rem;
          line-height: 1;
          cursor: pointer;
          opacity: 0.6;
          padding: 0 4px;
        }
        .subscription-banner-close:hover {
          opacity: 1;
        }
        .activate-plan-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          flex-wrap: wrap;
          background: #fffbeb;
          border: 1px solid #fcd34d;
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: var(--spacing-md);
        }
        .activate-plan-text strong {
          display: block;
          color: #92400e;
          font-size: 1rem;
          margin-bottom: 4px;
        }
        .activate-plan-text p {
          margin: 0;
          font-size: 0.9rem;
          color: #78350f;
          line-height: 1.5;
        }
        .activate-plan-error {
          margin-top: 8px !important;
          color: #991b1b !important;
          font-weight: 500;
        }
        .activate-plan-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .activate-plan-btn {
          background: #d97706;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 10px 20px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .activate-plan-btn:hover:not(:disabled) {
          background: #b45309;
        }
        .activate-plan-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .activate-plan-dismiss {
          background: none;
          border: none;
          color: #92400e;
          font-size: 0.85rem;
          cursor: pointer;
          text-decoration: underline;
        }
        .snow-total-icon {
          font-size: 1.25rem;
        }
        .snow-total-text {
          font-size: 1rem;
          font-weight: 600;
        }
        .hourly-forecast-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 12px;
        }
        .hourly-day-group {
          background: rgba(0,0,0,0.1);
          border-radius: 10px;
          padding: 12px;
        }
        .hourly-day-label {
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          opacity: 0.9;
          margin-bottom: 10px;
          padding-left: 4px;
        }
        .weather-forecast.hourly {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          justify-content: flex-start;
        }
        .weather-forecast.hourly .forecast-day {
          min-width: 48px;
          flex: 0 0 auto;
        }
        @media (max-width: 480px) {
          .weather-current {
            flex-direction: column;
            text-align: center;
          }
          .weather-temp-main {
            align-items: center;
          }
          .weather-details {
            align-items: center;
          }
        }
      `}</style>

      <PullToRefreshIndicator />

      {/* Main Content */}
      <div className="page-container">
        {dashboardError && (
          <div className="alert alert-danger" style={{ marginBottom: 'var(--spacing-md)' }}>{dashboardError}</div>
        )}

        {/* Post-checkout confirmation. Click anywhere on it to dismiss early. */}
        {subscriptionNotice === 'success' && (
          <div
            className="subscription-banner subscription-banner-success"
            role="status"
            onClick={() => setSubscriptionNotice(null)}
          >
            <span className="subscription-banner-icon">&#127881;</span>
            <div>
              <strong>Welcome to SnowTrack&trade; {purchasedPlan ? purchasedPlan.planName : getPlanName(user?.subscriptionTier)}!</strong>{' '}
              Your subscription is active.
            </div>
            <button
              type="button"
              className="subscription-banner-close"
              aria-label="Dismiss"
              onClick={() => setSubscriptionNotice(null)}
            >
              &times;
            </button>
          </div>
        )}

        {subscriptionNotice === 'cancelled' && (
          <div
            className="subscription-banner subscription-banner-info"
            role="status"
            onClick={() => setSubscriptionNotice(null)}
          >
            <span className="subscription-banner-icon">&#8505;&#65039;</span>
            <div>
              Subscription checkout was cancelled. You can try again from the{' '}
              <Link to="/pricing">Pricing page</Link>.
            </div>
            <button
              type="button"
              className="subscription-banner-close"
              aria-label="Dismiss"
              onClick={() => setSubscriptionNotice(null)}
            >
              &times;
            </button>
          </div>
        )}

        {/* New signups from the public intake form that the owner has not
            reviewed. A signup leaves the card when its edit form is saved or
            Mark reviewed is pressed (customers.intake_reviewed_at, migration
            078); the "New - From Form" badge on the Customers page uses the
            same column. Owner and admin only, like the endpoint, and absent
            when there is nothing to review. The Customers page shows the same
            notice (components/IntakeSignupsNotice.js). */}
        {isAdmin() && (
          <IntakeSignupsNotice
            items={intakeSignups}
            error={intakeSignupsError}
            reviewingId={reviewingId}
            onReview={reviewIntakeSignup}
            onMarkReviewed={markIntakeSignupReviewed}
          />
        )}

        {/* On-call customers who asked for service in the customer portal
            since the owner last dismissed this. The Customers page shows the
            same notice (components/ServiceRequestsNotice.js). */}
        {isAdmin() && (
          <ServiceRequestsNotice
            notice={serviceRequestsNotice}
            dismissing={dismissingServiceRequests}
            error={serviceRequestsNoticeError}
            onDismiss={dismissServiceRequests}
          />
        )}

        {/* Driveway sizes the customer stated and the owner has not agreed with
            yet. Hidden at zero, so a company that has had no intake signups
            never sees it, and it disappears the moment the last one is saved.

            Not shown to drivers: the Customers page is not on their nav, so the
            banner would point at somewhere they cannot go.

            Amber rather than the info blue, because this is work waiting on the
            owner rather than news. The colours are the activate-plan-card's, the
            page's existing "waiting on you" treatment, not a new one.

            Not dismissable, unlike the subscription banners above whose layout
            it borrows. There is nothing to dismiss - it goes away by doing the
            thing, and a dismiss button would let an owner hide unpriced
            customers from themselves. */}
        {!isDriver() && sizeConfirmationCount > 0 && (
          <div
            className="subscription-banner subscription-banner-warning"
            role="status"
            onClick={() => navigate('/customers', { state: { sizeUnconfirmedOnly: true } })}
          >
            <span className="subscription-banner-icon" aria-hidden="true">&#9888;&#65039;</span>
            <div>
              <strong>
                {sizeConfirmationCount} {sizeConfirmationCount === 1 ? 'property needs' : 'properties need'} a driveway size confirmed
              </strong>{' '}
              {sizeConfirmationCount === 1 ? 'A customer stated' : 'Customers stated'} their own driveway size at signup.
              Confirm or change it on the Customers page to set the price.
              {/* Each name opens the Customers page on that card. A property is
                  named by its customer, with its own address, because the
                  customer's card is where the property sits. stopPropagation
                  keeps the banner's own click, which opens the filtered list,
                  from firing on top of it. */}
              {sizeConfirmationItems.length > 0 && (
                <ul className="size-pending-list">
                  {sizeConfirmationItems.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        className="size-pending-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/customers', {
                            state: {
                              focusCustomerId: item.customerId,
                              focusPropertyId: item.isProperty ? item.id : null,
                              focusIsActive: item.cardIsActive,
                              focusIntakeStatus: item.cardIntakeStatus,
                            },
                          });
                        }}
                      >
                        {item.name || 'Unnamed customer'}
                        {item.isProperty && item.address ? ` (${item.address})` : ''}
                      </button>
                    </li>
                  ))}
                  {sizeConfirmationCount > sizeConfirmationItems.length && (
                    <li className="size-pending-more">
                      and {sizeConfirmationCount - sizeConfirmationItems.length} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Plan picked on the pricing page before this account existed. */}
        {showActivatePrompt && (
          <div className="activate-plan-card">
            <div className="activate-plan-text">
              <strong>Activate your {getPlanName(pendingPlan)} plan</strong>
              <p>
                You chose {getPlanName(pendingPlan)} when you signed up. Add a payment method
                to start it, and your trial storm event stays yours either way.
              </p>
              {activateError && (
                <p className="activate-plan-error" role="alert">{activateError}</p>
              )}
            </div>
            <div className="activate-plan-actions">
              <button
                type="button"
                className="activate-plan-btn"
                onClick={handleActivatePlan}
                disabled={activating}
              >
                {activating ? 'Starting checkout...' : `Activate ${getPlanName(pendingPlan)}`}
              </button>
              <button
                type="button"
                className="activate-plan-dismiss"
                onClick={() => setActivateDismissed(true)}
              >
                Not now
              </button>
            </div>
          </div>
        )}
        {/* Welcome */}
        <div className="page-header">
          <h1 className="page-title">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-muted">
            {user?.role === 'owner' && 'Owner'}
            {user?.role === 'admin' && 'Administrator'}
            {user?.role === 'crew_lead' && 'Crew Lead'}
            {user?.role === 'driver' && 'Driver'}
          </p>
        </div>

        {/* Subscription ended: read-only access */}
        <LapsedBanner />

        {/* Trial Status Banner */}
        {isOnTrial() && (
          <div className={`trial-banner ${isTrialExpired() ? 'trial-expired' : ''}`}>
            <div className="trial-banner-content">
              <span className="trial-icon">{isTrialExpired() ? '⚡' : '🎉'}</span>
              <div className="trial-text">
                {isTrialExpired() ? (
                  <>
                    <strong>Trial Complete!</strong> You've used your {TRIAL_STORM_LIMIT} free storm event.
                  </>
                ) : isTrialStormInProgress() ? (
                  <>
                    <strong>Free trial storm in progress.</strong> Ends {formatTrialStormEnd(getTrialStormEndsAt(), timezone)}.
                  </>
                ) : (
                  <>
                    <strong>Your first storm is free.</strong>
                  </>
                )}
              </div>
              {isTrialExpired() && (
                <Link to="/pricing" className="trial-upgrade-btn">Upgrade Now</Link>
              )}
            </div>
          </div>
        )}

        {/* New account checklist: owners and admins only. */}
        {isAdmin() && <GettingStartedCard />}

        {/* Season confirmations: who confirmed, declined or has not replied. */}
        {isAdmin() && <SeasonCard />}

        {/* Stats Grid. One picker sets the period for Total Routes and Total
            Stops together, so the two tiles never count different periods. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <label htmlFor="tilePeriod" style={{ fontSize: '0.8rem', color: 'var(--color-text-light)' }}>Routes and stops</label>
          <select
            id="tilePeriod"
            className="form-select"
            style={{ width: 'auto', padding: '4px 8px', fontSize: '0.8rem' }}
            value={tilePeriod}
            onChange={(e) => setTilePeriod(e.target.value)}
          >
            <option value="this">This season</option>
            <option value="last">Last season</option>
            <option value="all">All time</option>
          </select>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{tileStatsLoading || !tileStats ? '-' : tileStats.totalRoutes}</div>
            <div className="stat-label">Total Routes{tileStats && tileStats.label ? ` ${tileStats.label}` : ''}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: stats.activeRoutes > 0 ? 'var(--color-secondary)' : undefined }}>
              {loading ? '-' : stats.activeRoutes}
            </div>
            <div className="stat-label">Active Routes</div>
          </div>
          {!isDriver() && (
            <div className="stat-card">
              <div className="stat-value">{loading ? '-' : stats.totalCustomers}</div>
              <div className="stat-label">Customers</div>
            </div>
          )}
          <div className="stat-card">
            <div className="stat-value">{tileStatsLoading || !tileStats ? '-' : tileStats.totalStops}</div>
            <div className="stat-label">Total Stops{tileStats && tileStats.label ? ` ${tileStats.label}` : ''}</div>
          </div>
        </div>

        {/* Empty state for new users */}
        {!loading && stats.totalRoutes === 0 && (
          <div className="alert alert-info" style={{ textAlign: 'center', padding: '24px' }}>
            <h3 style={{ margin: '0 0 8px' }}>Welcome to {getAppName()}!</h3>
            <p style={{ margin: '0 0 16px', color: 'var(--color-text-light)' }}>Get started by creating your first route.</p>
            <button className="btn btn-primary" onClick={() => navigate('/routes')}>Create Route</button>
          </div>
        )}

        {/* Weather Widget */}
        <div className="weather-widget">
          {/* Weather location header */}
          {!weatherLoading && !weatherError && weather && (
            <div className="weather-header">
              <span className="weather-header-location">
                📍 {weatherLocationName || weather.current?.location || 'Weather'}
              </span>
              {weatherSettings?.weatherUseHome && weatherSettings?.weatherCity && (
                <button
                  className="weather-location-toggle"
                  onClick={usingCurrentLocation ? handleUseHomeLocation : handleUseCurrentLocation}
                  title={usingCurrentLocation ? 'Use home location' : 'Use current GPS location'}
                >
                  {usingCurrentLocation ? '🏠 Home' : '📍 Current'}
                </button>
              )}
            </div>
          )}
          {weatherLoading ? (
            <div className="weather-loading">
              <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
              <span>Loading weather...</span>
            </div>
          ) : weatherError ? (
            <div className="weather-error">
              <span className="weather-error-icon">☁️</span>
              <span>{weatherError}</span>
            </div>
          ) : weather ? (
            <>
              <div className="weather-current">
                <img
                  src={getWeatherIcon(weather.current.icon)}
                  alt={weather.current.condition}
                  className="weather-icon-large"
                />
                <div className="weather-temp-main">
                  <span className="weather-temp">{weather.current.temp}°F</span>
                  <span className="weather-condition">{weather.current.description}</span>
                  {weather.current.snow > 0 && (
                    <span className="weather-snow-current">❄️ {weather.current.snow}" snow</span>
                  )}
                </div>
                <div className="weather-details">
                  <span className="weather-detail">Feels like {weather.current.feelsLike}°F</span>
                  <span className="weather-detail">Wind: {weather.current.windSpeed} mph</span>
                </div>
              </div>

              {/* Daily/Hourly Toggle */}
              <div className="weather-toggle">
                <button
                  className={`weather-toggle-btn ${weatherView === 'daily' ? 'active' : ''}`}
                  onClick={() => setWeatherView('daily')}
                >
                  Daily
                </button>
                <button
                  className={`weather-toggle-btn ${weatherView === 'hourly' ? 'active' : ''}`}
                  onClick={() => setWeatherView('hourly')}
                >
                  Hourly
                </button>
              </div>

              {/* Daily Forecast */}
              {weatherView === 'daily' && weather.forecast && weather.forecast.length > 0 && (
                <>
                  {parseFloat(getTotalSnowfall(weather.forecast)) > 0 && (
                    <div className="snow-total-banner">
                      <span className="snow-total-icon">❄️</span>
                      <span className="snow-total-text">
                        {getTotalSnowfall(weather.forecast)}" total snowfall expected
                      </span>
                    </div>
                  )}
                  <div className="weather-forecast">
                    {weather.forecast.slice(0, 7).map((day, index) => (
                      <div key={index} className="forecast-day">
                        <span className="forecast-label">{formatForecastDate(day.date)}</span>
                        <img
                          src={getWeatherIcon(day.icon)}
                          alt={day.condition}
                          className="forecast-icon"
                        />
                        <span className="forecast-temp">{day.temp}°</span>
                        {parseFloat(day.snow) > 0 && (
                          <span className="forecast-snow">❄️ {day.snow}"</span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Hourly Forecast - Grouped by Day */}
              {weatherView === 'hourly' && weather.hourly && weather.hourly.length > 0 && (
                <>
                  {parseFloat(getTotalSnowfall(weather.hourly)) > 0 && (
                    <div className="snow-total-banner">
                      <span className="snow-total-icon">❄️</span>
                      <span className="snow-total-text">
                        {getTotalSnowfall(weather.hourly)}" snowfall next 72hrs
                      </span>
                    </div>
                  )}
                  <div className="hourly-forecast-container">
                    {groupHourlyByDay(weather.hourly).map((dayGroup, dayIndex) => (
                      <div key={dayIndex} className="hourly-day-group">
                        <div className="hourly-day-label">{dayGroup.label}</div>
                        <div className="weather-forecast hourly">
                          {dayGroup.hours.map((hour, index) => (
                            <div key={index} className="forecast-day">
                              <span className="forecast-label">{formatHourlyTime(hour.time)}</span>
                              <img
                                src={getWeatherIcon(hour.icon)}
                                alt={hour.condition}
                                className="forecast-icon"
                              />
                              <span className="forecast-temp">{hour.temp}°</span>
                              {parseFloat(hour.snow) > 0 && (
                                <span className="forecast-snow">❄️ {hour.snow}"</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : null}
        </div>

        {/* Quick Actions */}
        <h2 className="mt-lg mb-md">Quick Actions</h2>
        <div className="grid grid-2">
          <div className="mobile-card" onClick={() => navigate('/storm-mode')} style={{ cursor: 'pointer' }}>
            <div className="mobile-card-header">
              <div>
                <h3 className="mobile-card-title" style={{ color: 'var(--color-secondary)' }}>Storm Mode</h3>
                <p className="mobile-card-subtitle">Start servicing routes</p>
              </div>
            </div>
            <button className="btn btn-secondary btn-block">Start Storm Mode</button>
          </div>

          <div className="mobile-card" onClick={() => navigate('/routes')} style={{ cursor: 'pointer' }}>
            <div className="mobile-card-header">
              <div>
                <h3 className="mobile-card-title">Routes</h3>
                <p className="mobile-card-subtitle">View and manage routes</p>
              </div>
            </div>
            <button className="btn btn-primary btn-block">View Routes</button>
          </div>

          {!isDriver() && (
            <div className="mobile-card" onClick={() => navigate('/customers')} style={{ cursor: 'pointer' }}>
              <div className="mobile-card-header">
                <div>
                  <h3 className="mobile-card-title">Customers</h3>
                  <p className="mobile-card-subtitle">Manage customer list</p>
                </div>
              </div>
              <button className="btn btn-primary btn-block">View Customers</button>
            </div>
          )}

          {isAdmin() && (
            <div className="mobile-card financial-summary-card">
              <div className="mobile-card-header financial-header">
                <div>
                  <h3 className="mobile-card-title">
                    <span className="financial-icon">💰</span> Financial Summary
                  </h3>
                </div>
                <select
                  className="financial-period-select"
                  value={financialPeriod}
                  onChange={handleFinancialPeriodChange}
                >
                  <option value="this_month">This Month</option>
                  <option value="last_month">Last Month</option>
                  <option value="this_season">This Season</option>
                  <option value="ytd">Year to Date</option>
                  <option value="all_time">All Time</option>
                </select>
              </div>
              {financialSummaryLoading ? (
                <div className="financial-summary-loading">Loading...</div>
              ) : financialSummaryError ? (
                <div className="financial-summary-loading" style={{ color: 'var(--color-error)' }}>{financialSummaryError}</div>
              ) : financialSummary ? (
                <div className="financial-summary-stats">
                  <div className="financial-section">
                    <div className="financial-section-label">Revenue</div>
                    <div className="financial-row">
                      <span className="financial-label">Paid:</span>
                      <span className="financial-value financial-paid">{formatCurrency(financialSummary.revenuePaid)}</span>
                    </div>
                    <div className="financial-row">
                      <span className="financial-label">Unpaid:</span>
                      <span className="financial-value financial-unpaid">{formatCurrency(financialSummary.revenueUnpaid)}</span>
                    </div>
                    <div className="financial-row financial-row-total">
                      <span className="financial-label">Total:</span>
                      <span className="financial-value">{formatCurrency(financialSummary.revenueTotal)}</span>
                    </div>
                    {financialSummary.pendingDepthCount > 0 && (
                      <div className="financial-row financial-row-awaiting">
                        <span className="financial-label">Awaiting depth:</span>
                        <span className="financial-value">
                          {financialSummary.pendingDepthCount} stop{financialSummary.pendingDepthCount !== 1 ? 's' : ''}, not in totals
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="financial-row financial-row-expense">
                    <span className="financial-label">Expenses:</span>
                    <span className="financial-value financial-expense">-{formatCurrency(financialSummary.expensesTotal)}</span>
                  </div>
                  <div className="financial-row financial-row-profit">
                    <span className="financial-label">Net Profit:</span>
                    <span className={`financial-value ${financialSummary.netProfit >= 0 ? 'financial-profit-positive' : 'financial-profit-negative'}`}>
                      {financialSummary.netProfit >= 0 ? '' : '-'}{formatCurrency(Math.abs(financialSummary.netProfit))}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="financial-summary-loading">No data available</div>
              )}
              <Link to="/reports" className="btn btn-outline btn-block financial-link">
                View Details →
              </Link>
            </div>
          )}
        </div>

        {/* Trial Notice (if applicable) */}
        {user?.subscriptionStatus === 'trial' && (
          <div className="alert alert-info mt-lg">
            <strong>Free Trial Active</strong> - Your trial includes 1 complete storm
            event.{' '}
            <Link
              to="/pricing"
              style={{ fontWeight: 700, textDecoration: 'underline' }}
            >
              Upgrade
            </Link>{' '}
            to continue using {getAppName()}.
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
